import { createFileRoute } from '@tanstack/react-router'
import { formatearFecha } from '@/lib/fecha'
import { Banner } from '@/ui/Banner'
import { Esqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, Monto } from '@/ui/Numero'
import { PRESENTACION_CARGO, PRESENTACION_ETAPA, monedaPresentable, nombrePeriodo } from '@/dominio/cuenta/tipos'
import { useCuentaDespacho } from '@/dominio/despacho/useDespacho'
import type { CuentaDespacho } from '@/dominio/despacho/tipos'
import { Dato, EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDelDespacho } from './-comun'

/**
 * La cuenta del despacho con Agentical Group (`docs/adr/0021` §6): su etapa, su tarifa,
 * los cargos y los pagos con su e-CF, y cómo pagar.
 *
 * Las cinco etapas del cobro de los contribuyentes, con otra consecuencia: en
 * restringido el despacho no invita clientes nuevos, y suspendido por falta de
 * pago se cierran la cartera y el lote. **Nunca se cierra la entrada a cada
 * cliente**: su cumplimiento no depende de esta cuenta.
 */
export const Route = createFileRoute('/despacho/cuenta')({
  component: PantallaCuentaDespacho,
})

function PantallaCuentaDespacho() {
  const consulta = useCuentaDespacho()

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Cuenta con Agentical Group"
        descripcion="Lo que Agentical Group le cobra al despacho por la cartera, aparte de lo que paga cada cliente por lo suyo."
      />

      {consulta.isError ? (
        <ErrorDelDespacho error={consulta.error} queCargaba="la cuenta" onReintentar={() => void consulta.refetch()} />
      ) : consulta.data === undefined ? (
        <Esqueleto className="h-64 w-full" />
      ) : (
        <Contenido cuenta={consulta.data} />
      )}
    </div>
  )
}

function Contenido({ cuenta }: { cuenta: CuentaDespacho }) {
  const etapa = PRESENTACION_ETAPA[cuenta.estado.etapa]
  const t = cuenta.transferencia

  return (
    <>
      <Banner tono={etapa.tono} titulo={etapa.etiqueta}>
        {cuenta.estado.mensaje}
      </Banner>

      <div className="grid gap-4 md:grid-cols-2">
        <Tarjeta titulo="Tarifa">
          <dl>
            <Dato etiqueta="Precio mensual, antes de ITBIS">
              <Monto valor={cuenta.tarifa.precioMensual} moneda={monedaPresentable(cuenta.tarifa.moneda)} mostrarMoneda />
            </Dato>
            <Dato etiqueta="Se cobra">{cuenta.tarifa.activa && cuenta.tarifa.precioMensual !== '0.00' ? 'sí' : 'no'}</Dato>
            <Dato etiqueta="Saldo pendiente">
              <Monto valor={cuenta.estado.saldo} moneda={monedaPresentable(cuenta.estado.moneda)} mostrarMoneda />
            </Dato>
          </dl>
          {cuenta.tarifa.precioMensual === '0.00' ? (
            <p className="mt-2 text-etiqueta text-gris-600">Agentical Group todavía no le fijó precio a la cartera: no se cobra nada.</p>
          ) : null}
        </Tarjeta>

        <Tarjeta titulo="Cómo pagar">
          {t?.publicados ? (
            <dl>
              <Dato etiqueta="Banco">{t.banco}</Dato>
              <Dato etiqueta="Cuenta">
                <span className="numero">
                  {t.tipoCuenta} {t.numeroCuenta}
                </span>
              </Dato>
              <Dato etiqueta="Titular">{t.titular}</Dato>
              {t.correo ? <Dato etiqueta="Comprobante de la transferencia a">{t.correo}</Dato> : null}
              <Dato etiqueta="Concepto">el número del cargo</Dato>
            </dl>
          ) : (
            <p className="text-cuerpo text-gris-600">Agentical Group no ha publicado todavía los datos para transferir.</p>
          )}
        </Tarjeta>
      </div>

      <Tarjeta titulo="Cargos" relleno={false}>
        {cuenta.cargos.length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Ningún cargo." />
        ) : (
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300 text-left text-etiqueta font-semibold text-gris-600">
                <th scope="col" className="px-3 py-2">Cargo</th>
                <th scope="col" className="px-3 py-2">Período</th>
                <th scope="col" className="px-3 py-2">Vence</th>
                <th scope="col" className="px-3 py-2 text-right">Total</th>
                <th scope="col" className="px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {cuenta.cargos.map((c) => (
                <tr key={c.id} className="border-b border-gris-200 last:border-b-0">
                  <td className="numero px-3 py-2">{c.numero}</td>
                  <td className="px-3 py-2">{nombrePeriodo(c.periodo.slice(0, 7))}</td>
                  <td className="numero px-3 py-2">{formatearFecha(c.venceEl)}</td>
                  <td className="px-3 py-2 text-right">
                    <Monto valor={c.total} moneda={monedaPresentable(c.moneda)} />
                  </td>
                  <td className="px-3 py-2">
                    <InsigniaEstado tono={PRESENTACION_CARGO[c.estado].tono}>{PRESENTACION_CARGO[c.estado].etiqueta}</InsigniaEstado>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {cuenta.pagos.length > 0 ? (
        <Tarjeta titulo="Pagos" relleno={false}>
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300 text-left text-etiqueta font-semibold text-gris-600">
                <th scope="col" className="px-3 py-2">Fecha</th>
                <th scope="col" className="px-3 py-2">Referencia</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2">Comprobante</th>
              </tr>
            </thead>
            <tbody>
              {cuenta.pagos.map((p) => (
                <tr key={p.id} className="border-b border-gris-200 last:border-b-0">
                  <td className="numero px-3 py-2">{formatearFecha(p.pagadoEl)}</td>
                  <td className="px-3 py-2">{p.referencia}</td>
                  <td className="px-3 py-2 text-right">
                    <Monto valor={p.monto} moneda={monedaPresentable(p.moneda)} />
                  </td>
                  <td className="px-3 py-2">
                    {p.comprobanteENCF ? (
                      <ENCF valor={p.comprobanteENCF} />
                    ) : (
                      <span className="text-etiqueta text-gris-600">{p.comprobanteDetalle ?? 'pendiente'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      ) : null}
    </>
  )
}
